import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import React, {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import styles from './MergeFieldInterpretationsTable.module.scss';
import DragAndDropIcon from '@lmt/unicorn-shared/app/icons/drag-and-drop.svg?react';
import CancelIcon from '@lmt/unicorn-shared/app/icons/cancel.svg?react';
import { CSS } from '@dnd-kit/utilities';
import i18next from 'i18next';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import clsx from 'clsx';
import { Input } from '../../common/ui-kit/inputs/Input';
import { ScrollToTop } from '../../common/table/helper';
import { IconButton } from '../../common/ui-kit/IconButton';
import {
  ConditionType,
  MergeFieldInterpretation,
  QueryFactory,
} from '@lmt/shared-types';
import { UseFormReturn } from 'react-hook-form';
import { UniLink } from '../../common/ui-kit/buttons/UniLink';
import { ButtonColor } from '../../common/ui-kit/buttons/Button';
import AddIcon from '@lmt/unicorn-shared/app/icons/plus-blue.svg?react';
import { isEmpty } from '../../measurements/copy-datablock-field/common';

const conditionOptions = [
  { value: ConditionType.Equal, displayValue: '=' },
  { value: ConditionType.Greater, displayValue: '>' },
  { value: ConditionType.GreaterOrEqual, displayValue: '>=' },
  { value: ConditionType.Less, displayValue: '<' },
  { value: ConditionType.LessOrEqual, displayValue: '<=' },
  { value: ConditionType.NotEqual, displayValue: '<>' },
  { value: ConditionType.GreaterOrEqualAndLess, displayValue: '>= - <' },
  {
    value: ConditionType.GreaterOrEqualAndLessOrEqual,
    displayValue: '>= - <=',
  },
  { value: ConditionType.GreaterAndLessOrEqual, displayValue: '> - <=' },
  { value: ConditionType.GreaterAndLess, displayValue: '> - <' },
];

export const simpleConditions = [
  ConditionType.Equal,
  ConditionType.Greater,
  ConditionType.GreaterOrEqual,
  ConditionType.Less,
  ConditionType.LessOrEqual,
  ConditionType.NotEqual,
];

export const rangeConditions = [
  ConditionType.GreaterOrEqualAndLess,
  ConditionType.GreaterOrEqualAndLessOrEqual,
  ConditionType.GreaterAndLessOrEqual,
  ConditionType.GreaterAndLess,
];

function validateField(
  key: string,
  data: MergeFieldInterpretation,
  showErrors: boolean,
): string | undefined {
  if (!showErrors) {
    return undefined;
  }
  switch (key) {
    case 'value':
      if (simpleConditions.includes(data.condition)) {
        if (isEmpty(data.value)) {
          return ' ';
        }
      } else {
        if (!!data.value) {
          return ' ';
        }
      }
      return undefined;
    case 'minValue':
    case 'maxValue':
      if (rangeConditions.includes(data.condition)) {
        if (isEmpty(data[key])) {
          return ' ';
        }
        if (data.minValue && data.maxValue && data.minValue >= data.maxValue) {
          return ' ';
        }
      } else {
        if (!!data[key]) {
          return ' ';
        }
      }
      return undefined;
    default:
      return undefined;
  }
}

const DragHandle = React.forwardRef<HTMLDivElement>((props, ref) => (
  <div
    ref={ref}
    {...props}
    title={i18next.t(
      'documents.query_builder.merge_field_rules.drag_and_drop_tooltip',
    )}
  >
    <DragAndDropIcon className={styles.icon} />
  </div>
));

const SortableRowWrapper = (props: RowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props.data.position + 1 });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <Row
      {...props}
      {...attributes}
      listeners={listeners}
      style={style}
      ref={setNodeRef}
    />
  );
};

const Row = React.forwardRef<HTMLDivElement, RowProps>((props, ref) => {
  const {
    data,
    removeLine,
    editLine,
    expressionError,
    listeners,
    style,
    showErrors,
    isReadOnly,
    ...other
  } = props;

  return (
    <div {...other} className={clsx(styles.row, styles.rowData)} style={style}>
      <div>{!isReadOnly && <DragHandle ref={ref} {...listeners} />}</div>
      <div
        className={styles.cell}
        data-test-id="interpretation-condition-field"
      >
        {isReadOnly ? (
          <Input
            readOnly={isReadOnly}
            value={
              conditionOptions.find((x) => x.value === data.condition)
                ?.displayValue
            }
          />
        ) : (
          <DropDownInput
            useIdFunctionAsValue={true}
            variant={'formInput'}
            options={conditionOptions}
            getOptionLabel="displayValue"
            idFunction="value"
            value={data.condition ?? ConditionType.Equal}
            required={true}
            enableSearch={true}
            onValueChanged={(value) => {
              editLine({
                ...data,
                condition: value.value,
                value: rangeConditions.includes(value.value)
                  ? null
                  : data.value,
                minValue: rangeConditions.includes(value.value)
                  ? data.minValue
                  : null,
                maxValue: rangeConditions.includes(value.value)
                  ? data.maxValue
                  : null,
              });
            }}
          />
        )}
      </div>
      <div className={styles.cell} data-test-id="interpretation-value-field">
        <Input
          disabled={!simpleConditions.includes(data.condition)}
          readOnly={isReadOnly}
          type="number"
          value={data.value ?? ''}
          onChange={(value) =>
            editLine({
              ...data,
              value: !value.currentTarget.value
                ? null
                : Number.parseFloat(value.currentTarget.value),
            })
          }
          errorText={validateField('value', data, showErrors)}
        />
      </div>
      <div
        className={styles.cell}
        data-test-id="interpretation-min-value-field"
      >
        <Input
          disabled={!rangeConditions.includes(data.condition)}
          readOnly={isReadOnly}
          type="number"
          value={data.minValue ?? ''}
          onChange={(value) =>
            editLine({
              ...data,
              minValue: !value.currentTarget.value
                ? null
                : Number.parseFloat(value.currentTarget.value),
            })
          }
          errorText={validateField('minValue', data, showErrors)}
        />
      </div>
      <div
        className={styles.cell}
        data-test-id="interpretation-max-value-field"
      >
        <Input
          disabled={!rangeConditions.includes(data.condition)}
          readOnly={isReadOnly}
          type="number"
          value={data.maxValue ?? ''}
          onChange={(value) =>
            editLine({
              ...data,
              maxValue: !value.currentTarget.value
                ? null
                : Number.parseFloat(value.currentTarget.value),
            })
          }
          errorText={validateField('maxValue', data, showErrors)}
        />
      </div>
      <div className={styles.cell} data-test-id="interpretation-entry-field">
        <Input
          readOnly={isReadOnly}
          value={data.entry ?? undefined}
          onChange={(value) =>
            editLine({
              ...data,
              entry: value.currentTarget.value,
            })
          }
        />
      </div>
      {!isReadOnly && (
        <div
          className={clsx(styles.cell, styles.deleteCell)}
          onClick={(event: any) => event.stopPropagation()}
          data-test-id="interpretation-cross-button"
        >
          <IconButton
            color="grey"
            iconComponent={<CancelIcon className={styles.optionIcon} />}
            onClick={() => removeLine(data.position)}
          />
        </div>
      )}
    </div>
  );
});

type Props = {
  form: UseFormReturn<QueryFactory.CreateMergeFieldDto>;
  showErrors: boolean;
  isReadOnly: boolean;
};
type RowProps = {
  form: UseFormReturn<QueryFactory.CreateMergeFieldDto>;
  showErrors: boolean;
  data: MergeFieldInterpretation;
  editLine: (line: MergeFieldInterpretation) => void;
  removeLine: (position: number) => void;
  expressionError?: string | null | undefined;
  style?: CSSProperties | undefined;
  listeners?: any;
  isReadOnly: boolean;
};
export const MergeFieldInterpretationsTable: FC<Props> = React.memo(
  function MergeFieldInterpretationsTable(props) {
    const i18next = useScopedTranslation(
      'documents.query_builder.merge_field_rules',
    );
    const i18n = useScopedTranslation('documents.query_builder');
    const sensors = useSensors(useSensor(PointerSensor));
    const [activeId, setActiveId] = useState<number | null>(null);
    const { form, isReadOnly } = props;

    const tableRef = useRef<HTMLDivElement>(null);
    const [interpretations] = form.watch(['interpretations']);

    const onAddNewLine = useCallback(
      (defaultType: ConditionType) => {
        const newLine: MergeFieldInterpretation = {
          position: interpretations?.length ?? 0,
          condition: defaultType,
          value: null,
          minValue: null,
          maxValue: null,
          entry: '',
        };
        form.setValue('interpretations', [...(interpretations ?? []), newLine]);
      },
      [interpretations],
    );

    const AddOptions = (
      <div>
        <UniLink
          title={i18n.t('add_interpretation')}
          color={ButtonColor.Primary}
          icon={<AddIcon />}
          onClick={() => onAddNewLine(ConditionType.Equal)}
          className={styles.optionButton}
        />
        <UniLink
          title={i18n.t('add_range_condition')}
          color={ButtonColor.Primary}
          icon={<AddIcon />}
          onClick={() => onAddNewLine(ConditionType.GreaterOrEqualAndLess)}
          className={styles.optionButton}
        />
      </div>
    );

    useEffect(() => {
      if (tableRef.current !== null) {
        ScrollToTop(tableRef.current);
      }
    }, [interpretations]);

    const onEdit = useCallback(
      (line: MergeFieldInterpretation) => {
        if (!interpretations) return;
        form.setValue(
          'interpretations',
          interpretations.map((data) =>
            data.position === line.position ? line : data,
          ),
        );
      },
      [interpretations],
    );
    const onRemove = useCallback(
      (position: number) => {
        if (!interpretations) return;
        form.setValue(
          'interpretations',
          interpretations
            .filter((d) => d.position !== position)
            .map((data, index) => {
              return {
                ...data,
                position: index,
              };
            }),
        );
      },
      [interpretations],
    );
    const onSortEnd = useCallback(
      async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        if (!interpretations) return;
        const items = interpretations;
        const oldIndex = items.findIndex((x) => x.position + 1 == active.id);
        const newIndex = items.findIndex((x) => x.position + 1 == over.id);

        const newData = arrayMove(interpretations, oldIndex, newIndex);
        newData.forEach((item, index) => {
          item.position = index;
        });
        form.setValue('interpretations', newData);
      },
      [interpretations],
    );

    return (
      <>
        {interpretations && interpretations.length > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e) => {
              setActiveId(e.active.id as number);
            }}
            onDragEnd={(e) => {
              onSortEnd(e);
              setActiveId(null);
            }}
          >
            <div className={clsx(styles.container)}>
              <div
                className={clsx(styles.header, styles.row)}
                data-test-id="interpretation-header"
              >
                <div className={styles.headerCell}></div>
                <div className={styles.headerCell}>
                  {i18next.t('columns.condition')}
                </div>
                <div className={styles.headerCell}>
                  {i18next.t('columns.value')}
                </div>
                <div className={styles.headerCell}>
                  {i18next.t('columns.min_value')}
                </div>
                <div className={styles.headerCell}>
                  {i18next.t('columns.max_value')}
                </div>
                <div className={styles.headerCell}>
                  {i18next.t('columns.entry')}
                </div>
                <div></div>
                <div></div>
              </div>
              <SortableContext
                items={interpretations.map((x) => x.position + 1)}
                strategy={verticalListSortingStrategy}
              >
                {interpretations.map((row) => (
                  <SortableRowWrapper
                    form={props.form}
                    key={row.position}
                    data={row}
                    editLine={onEdit}
                    removeLine={onRemove}
                    showErrors={props.showErrors}
                    data-test-id="condition-row"
                    isReadOnly={isReadOnly}
                  />
                ))}
              </SortableContext>
            </div>
          </DndContext>
        )}

        {!isReadOnly && AddOptions}
      </>
    );
  },
);
